<template>
  <nav class="bg-white px-[21px] pb-[21px] pt-3.5" :aria-labelledby="headingId">
    <span :id="headingId" class="text-text-xl font-medium uppercase">Contents</span>
    <ul class="ml-[7px] mt-3.5 list-none border-l border-gray-400 p-0">
      <li
        v-for="entry in entries"
        :key="entry.target"
        class="relative mb-[27px] pl-[27px] text-text-xl last:mb-0"
      >
        <span
          class="absolute left-[-5px] top-2 h-[9px] w-[9px] rounded-full"
          :class="entry.target === currentTarget ? 'bg-blue-400' : 'bg-gray-400'"
          aria-hidden="true"
        />
        <a
          :href="`#${entry.target}`"
          class="block font-medium hover:underline"
          :class="entry.target === currentTarget ? 'text-blue-400' : 'text-gray-900'"
          :aria-current="entry.target === currentTarget ? 'location' : undefined"
          @click.prevent="scrollToSection(entry)"
        >{{ entry.text }}</a>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

interface TocEntry {
  text: string
  target: string
}

const props = defineProps<{ entries: TocEntry[] }>()

const NAVBAR_SCROLL_OFFSET_PX = 70
const SCROLLSPY_OFFSET_PX = 80

const headingId = 'collection-toc-heading'
const analytics = getAnalyticsTracker()
const currentTarget = ref<string | undefined>()

function sectionTop(target: string) {
  return document.getElementById(target)?.getBoundingClientRect().top ?? Infinity
}

function isScrolledToBottom() {
  return window.scrollY + window.innerHeight >= document.documentElement.scrollHeight
}

function updateCurrentTarget() {
  const lastEntry = props.entries.at(-1)
  if (lastEntry && isScrolledToBottom()) {
    currentTarget.value = lastEntry.target
    return
  }
  currentTarget.value = props.entries.findLast(({ target }) => sectionTop(target) <= SCROLLSPY_OFFSET_PX)?.target
}

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

function scrollToSection(entry: TocEntry) {
  const section = document.getElementById(entry.target)
  if (section) {
    window.scrollTo({
      top: window.scrollY + section.getBoundingClientRect().top - NAVBAR_SCROLL_OFFSET_PX,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    })
    section.focus({ preventScroll: true })
  }
  analytics.sendEvent(EVENT_NAMES.CURATED_COLLECTION.CLICKED_TOC, { toc_section: entry.text })
}

onMounted(() => {
  updateCurrentTarget()
  window.addEventListener('scroll', updateCurrentTarget, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateCurrentTarget)
})
</script>
